import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytesToMB } from '@utils/formatBytes';

interface FileChipProps {
  file: File;
  thumbnail?: string;
  canRemove: boolean;
  onRemove: (file: File) => void;
  progress?: {
    transferred: number;
    total?: number;
    status: string;
  };
}

export function FileChip({ file, thumbnail, canRemove, onRemove, progress }: FileChipProps) {
  const { t } = useTranslation();
  const isImage = Boolean(thumbnail);
  const baseName = file.name.includes('.') 
    ? file.name.slice(0, file.name.lastIndexOf('.'))
    : file.name;

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(file);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="file-chip" onClick={handleClick}>
      {canRemove && (
        <button
          onClick={handleRemove}
          aria-label={`Remove ${file.name}`}
          className="file-chip-remove"
        >
          ×
        </button>
      )}
      
      <div className="file-chip-preview">
        {isImage && thumbnail && (
          <img src={thumbnail} alt={file.name} />
        )}
      </div>
      
      <div className="file-chip-name">{baseName}</div>
      <div className="file-chip-size">{formatBytesToMB(file.size, 2, t)}</div>
      
      {progress && (
        <div className="file-chip-progress">
          <FileChipProgress {...progress} />
        </div>
      )}
    </div>
  );
}

interface FileChipProgressProps {
  transferred: number;
  total?: number;
  status: string;
}

function FileChipProgress({ transferred, total, status }: FileChipProgressProps) {
  const { t } = useTranslation();
  const percent = total ? Math.min(100, Math.round((transferred / total) * 100)) : 0;
  const pctClass = `w-pct-${percent}`;

  return (
    <div>
      <div className="progress-bar" role="progressbar" aria-valuenow={percent}>
        <span className={pctClass} />
      </div>
      <small>{formatBytesToMB(transferred, 2, t)} {t('upload.sent')}</small>
    </div>
  );
}
